import { zodResolver } from '@hookform/resolvers/zod';
import { Form } from '@repo/shadcn';
import { ReactNode, forwardRef, useImperativeHandle } from 'react';
import {
    Control,
    DefaultValues,
    FieldValues,
    FormState,
    Path,
    SubmitHandler,
    UseFormReturn,
    useForm,
} from 'react-hook-form';
import { ZodType, z } from 'zod';

export type IwFormRef<T extends FieldValues> = {
    getValues: () => T;
    reset: (values?: Partial<T>) => void;
    setValue: (name: keyof T, value: T[keyof T]) => void;
    formState: FormState<T>;
    control: Control<T>;
    form: UseFormReturn<T>;
};

export type IwFormProps<TSchema extends ZodType> = {
    schema: TSchema;
    initialValues: Partial<z.infer<TSchema>>;
    onSubmit: SubmitHandler<z.infer<TSchema>>;
    children: ReactNode;
    mode?: 'onChange' | 'onBlur' | 'onSubmit' | 'all';
    logErrors?: boolean;
} & React.ComponentPropsWithoutRef<'form'>;

/**
 * A generic form component.
 * @param schema The schema of the form.
 * @param initialValues The initial values of the form.
 * @param onSubmit The submit handler of the form.
 * @param children The children of the form.
 * @param ref The reference of the form.
 *
 * @returns The generic form component.
 */

export const IwForm = forwardRef(function IwForm<TSchema extends ZodType>(
    {
        initialValues,
        schema,
        onSubmit,
        children,
        mode = 'onChange',
        logErrors = false,
        ...props
    }: IwFormProps<TSchema>,
    ref: React.Ref<IwFormRef<z.infer<TSchema>>>,
) {
    const form = useForm<z.infer<TSchema>>({
        defaultValues: initialValues as DefaultValues<z.infer<TSchema>>,
        resolver: zodResolver(schema),
        mode,
    });

    if (logErrors) {
        const {
            formState: { errors },
        } = form;

        console.error(errors);
    }

    useImperativeHandle(ref, () => {
        type TFormValues = z.infer<TSchema>;

        return {
            getValues: form.getValues,
            reset: (values?: Partial<TFormValues>) =>
                form.reset(values as TFormValues),
            setValue: (
                name: keyof TFormValues,
                value: TFormValues[keyof TFormValues],
            ) => form.setValue(name as Path<TFormValues>, value),
            formState: form.formState,
            control: form.control,
            form: form,
        };
    });

    return (
        <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} {...props}>
                {children}
            </form>
        </Form>
    );
}) as <TSchema extends ZodType>(
    props: IwFormProps<TSchema> & {
        ref?: React.Ref<IwFormRef<z.infer<TSchema>>>;
    },
) => JSX.Element;
